import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from '../config/api';
import { errorMessage } from "../utils/errorMessage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Send, Loader2, FileX, CheckCircle2, Building2, Banknote, ClipboardCheck, FlaskConical, XCircle, Mail, HelpCircle } from "lucide-react";

import { LOGO_URL } from "../config/constants";
import SmartClientMatterSearch from "../components/SmartClientMatterSearch";

/*
 * BM-H3768 (Vanessa, 6 Oct 2026): "simplify the number of questions and allow
 * ledger and compliance data to be incorporated so not relying on users'
 * recollection. does it kick off a file closure email to the client?"
 *
 * The form used to ask about 22 questions from memory over six steps. It now
 * reads the ledger and compliance records (GET /file-closure/{ref}/records),
 * refuses to close while any record is short of clear, asks five things the
 * records cannot know, and shows the closure email to the client for the
 * closer to edit before it is sent. The server re-reads the records on submit
 * and refuses a closure they forbid (backend/hub/file_closure.py).
 */

const INPUT = "bg-[rgba(26,37,71,0.85)] border-[rgba(99,102,241,0.3)] text-white placeholder:text-txt-muted";

export const parseMoney = (raw) => {
  const s = String(raw ?? "").replace(/[£,\s]/g, "");
  if (!/^-?\d+(\.\d+)?$/.test(s)) return null;
  return Math.round(parseFloat(s) * 100) / 100;
};

const gbp = (n) => `£${Number(n).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const currentUserEmail = () => {
  try {
    return (sessionStorage.getItem("bonsai_user_email") || localStorage.getItem("bonsai_user_email") || "").toLowerCase();
  } catch { return ""; }
};

const STATE_STYLE = {
  ok: { icon: CheckCircle2, text: "text-teal", box: "border-teal/30 bg-teal/5", word: "Clear" },
  unknown: { icon: HelpCircle, text: "text-status-red", box: "border-red-500/50 bg-status-red/10", word: "Could not read: stops closure" },
  blocked: { icon: XCircle, text: "text-status-red", box: "border-red-500/50 bg-status-red/10", word: "Stops closure" },
};

/** Ledger balance against what CaseMinder records on account. */
export const ledgerDiffers = (records, declared) => {
  const moa = records?.money_on_account;
  const d = parseMoney(declared);
  return moa !== null && moa !== undefined && d !== null && Math.abs(d - moa) >= 0.005;
};

/** What still stops the closure, in the order the form asks. Mirrors closure_problems on the server. */
export const outstandingItems = (formData, records) => {
  const out = [];
  if (!records) return ["Choose the matter to close."];
  // BM-H3768 (Vanessa, 6 Oct 2026): anything short of clear stops the closure; no reason gets past it.
  (records.checks || []).filter(c => c.state !== "ok").forEach(c => out.push(c.detail));
  const declared = parseMoney(formData.declared_ledger_balance);
  if (declared === null) out.push("Give the client ledger balance (£0.00 if clear).");
  else if (Math.abs(declared) >= 0.005) out.push(`The client ledger shows ${gbp(declared)} still held. Return it before the file can close.`);
  else if (ledgerDiffers(records, formData.declared_ledger_balance)) out.push("The ledger balance differs from what CaseMinder records. Correct the record first.");
  (records.confirmations || []).forEach(c => { if (formData.closure_confirmations[c.key] !== true) out.push(`Confirm: ${c.label}`); });
  const e = formData.closure_email;
  if (e.send) {
    if (!e.to.trim()) out.push("Give the client's email address for the closure email.");
    if (!e.subject.trim() || !e.body.trim()) out.push("The closure email needs a subject and a message.");
  } else if (!e.not_sent_reason.trim()) out.push("Say how the client is being told the file is closed.");
  return out;
};

export function RecordsStep({ records, formData, updateField }) {
  const declared = parseMoney(formData.declared_ledger_balance);
  const nonZero = declared !== null && Math.abs(declared) >= 0.005;
  const differs = ledgerDiffers(records, formData.declared_ledger_balance);
  const group = (g) => (records.checks || []).filter(c => c.group === g);
  const renderCheck = (c) => {
    const st = STATE_STYLE[c.state] || STATE_STYLE.unknown;
    const Icon = st.icon;
    return (
      <div key={c.key} className={`p-3 rounded-md border ${st.box}`} data-testid={`closure-check-${c.key}`} data-state={c.state}>
        <div className="flex items-start gap-2">
          <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${st.text}`} />
          <div className="flex-1">
            <p className="text-sm text-white font-medium">{c.label} <span className={`text-xs font-normal ${st.text}`}>{st.word}</span></p>
            <p className="text-xs text-txt-muted mt-0.5">{c.detail}</p>
          </div>
        </div>
      </div>
    );
  };
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3"><div className="p-2 rounded-md bg-status-amber/10"><Banknote className="w-5 h-5 text-status-amber" /></div><h2 className="text-xl font-semibold text-white">What the records say</h2></div>
      <p className="text-sm text-txt-muted">Read from CaseMinder for {records.matter_reference}. Anything not clear has to be put right on the record before the file can close.</p>
      <div className="space-y-2"><h3 className="text-sm font-semibold text-white">Ledger</h3>{group("ledger").map(renderCheck)}</div>

      <div className={`p-4 rounded-md border ${nonZero || differs ? 'bg-status-red/10 border-red-500/50' : 'bg-navy-card border-navy-border'}`}>
        <Label className="text-sm font-medium text-txt block">Client ledger balance *</Label>
        <p className="text-xs text-txt-muted mt-1 mb-2">Filled in from money on account in CaseMinder. Change it only if the client ledger says otherwise.</p>
        <Input value={formData.declared_ledger_balance} onChange={(e) => updateField("declared_ledger_balance", e.target.value)}
          placeholder="£0.00" className={`${INPUT} font-mono`} data-testid="declared-ledger-balance" />
        {nonZero ? (
          <p className="mt-2 text-sm text-status-red" data-testid="ledger-blocks">Money is still held. Return it before the file can close.</p>
        ) : differs && (
          <p className="mt-2 text-sm text-status-red" data-testid="ledger-blocks">CaseMinder records {gbp(records.money_on_account)} on account. Correct the record before the file can close.</p>
        )}
      </div>

      <div className="space-y-2"><h3 className="text-sm font-semibold text-white">Compliance</h3>{group("compliance").map(renderCheck)}</div>
      {records.retention && (
        <p className="text-xs text-txt-muted" data-testid="closure-retention">
          Keep the file for {records.retention.years} years after closure. {records.retention.basis}
        </p>
      )}
    </div>
  );
}

export function ConfirmStep({ confirmations, formData, setConfirmation }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3"><div className="p-2 rounded-md bg-blue-500/10"><ClipboardCheck className="w-5 h-5 text-blue-300" /></div><h2 className="text-xl font-semibold text-white">What only you can confirm</h2></div>
      <div className="space-y-3">
        {(confirmations || []).map(c => (
          <div key={c.key} className="p-4 bg-navy-card rounded-md border border-navy-border flex items-start gap-3">
            <Checkbox id={`confirm-${c.key}`} checked={formData.closure_confirmations[c.key] === true}
              onCheckedChange={(v) => setConfirmation(c.key, v === true)} className="mt-0.5" data-testid={`confirm-${c.key}`} />
            <Label htmlFor={`confirm-${c.key}`} className="text-sm text-txt cursor-pointer">{c.label}</Label>
          </div>
        ))}
      </div>
    </div>
  );
}

export function EmailStep({ formData, setEmail }) {
  const e = formData.closure_email;
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3"><div className="p-2 rounded-md bg-teal/10"><Mail className="w-5 h-5 text-teal" /></div><h2 className="text-xl font-semibold text-white">Closure email to the client</h2></div>
      <div className="p-4 bg-navy-card rounded-md border border-navy-border flex items-start gap-3">
        <Checkbox id="closure-email-send" checked={e.send} onCheckedChange={(v) => setEmail("send", v === true)} data-testid="closure-email-send" />
        <Label htmlFor="closure-email-send" className="text-sm text-txt cursor-pointer">Send this email to the client from the fee earner's mailbox when I submit. It tells them the file is closed and our retainer has ended.</Label>
      </div>
      {e.send ? (
        <div className="space-y-3" data-testid="closure-email-preview">
          <div><Label className="text-sm text-txt">To</Label><Input value={e.to} onChange={(ev) => setEmail("to", ev.target.value)} placeholder="client@example.com" className={INPUT} data-testid="closure-email-to" /><p className="text-xs text-txt-muted mt-1">Separate several addresses with commas.</p></div>
          <div><Label className="text-sm text-txt">Subject</Label><Input value={e.subject} onChange={(ev) => setEmail("subject", ev.target.value)} className={INPUT} /></div>
          <div><Label className="text-sm text-txt">Message (you can edit it)</Label><Textarea value={e.body} onChange={(ev) => setEmail("body", ev.target.value)} rows={14} className={`${INPUT} font-sans`} data-testid="closure-email-body" /></div>
        </div>
      ) : (
        <div><Label className="text-sm text-txt">How is the client being told the file is closed? *</Label><Textarea value={e.not_sent_reason} onChange={(ev) => setEmail("not_sent_reason", ev.target.value)} placeholder="For example, a closing letter sent by post" rows={2} className={INPUT} data-testid="closure-email-not-sent-reason" /></div>
      )}
    </div>
  );
}

export default function FileClosureForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [records, setRecords] = useState(null);
  const [recordsStatus, setRecordsStatus] = useState(null); // 'loading' | 'loaded' | 'error'
  const [recordsError, setRecordsError] = useState("");
  const [bodyEdited, setBodyEdited] = useState(false);

  const [formData, setFormData] = useState({
    form_type: "file_closure",
    matter_reference: "",
    client_name: "",
    responsible_fee_earner: "",
    completer_email: currentUserEmail(),
    submitter_email: currentUserEmail(),
    declared_ledger_balance: "",
    closure_confirmations: {},
    closure_email: { send: true, to: "", subject: "", body: "", not_sent_reason: "" },
    is_test_submission: false,
  });

  const totalSteps = 4;
  const updateField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  const setConfirmation = (key, value) => setFormData(prev => ({ ...prev, closure_confirmations: { ...prev.closure_confirmations, [key]: value } }));
  const setEmail = (key, value) => {
    if (key === "body") setBodyEdited(true);
    setFormData(prev => ({ ...prev, closure_email: { ...prev.closure_email, [key]: value } }));
  };

  const loadRecords = useCallback(async (ref) => {
    if (!ref) return;
    setRecordsStatus("loading");
    setRecordsError("");
    try {
      const res = await axios.get(`${API}/file-closure/${encodeURIComponent(ref)}/records`);
      const r = res.data;
      setRecords(r);
      setRecordsStatus("loaded");
      setBodyEdited(false);
      setFormData(prev => ({
        ...prev,
        client_name: r.client_name || prev.client_name,
        responsible_fee_earner: r.fee_earner || prev.responsible_fee_earner,
        declared_ledger_balance: r.money_on_account === null || r.money_on_account === undefined ? "" : gbp(r.money_on_account),
        closure_email: { ...prev.closure_email, to: (r.client_emails || []).join(", "),
          subject: r.email_draft?.subject || "", body: r.email_draft?.body || "" },
      }));
    } catch (error) {
      setRecords(null);
      setRecordsStatus("error");
      setRecordsError(errorMessage(error, "Could not read the records for this matter"));
    }
  }, []);

  // Pre-populate from URL params (e.g. from final invoice email link)
  useEffect(() => {
    const matterRef = searchParams.get('matter_reference');
    const clientName = searchParams.get('client_name');
    if (matterRef || clientName) {
      setFormData(prev => ({ ...prev, ...(matterRef && { matter_reference: matterRef }), ...(clientName && { client_name: clientName }) }));
      if (matterRef) loadRecords(matterRef);
    }
  }, [searchParams, loadRecords]);

  // The money sentence in the draft follows the ledger balance given, until the closer edits the message.
  useEffect(() => {
    if (currentStep !== 4 || bodyEdited || !formData.matter_reference) return;
    const bal = parseMoney(formData.declared_ledger_balance);
    if (bal === null) return;
    axios.get(`${API}/file-closure/${encodeURIComponent(formData.matter_reference)}/email-draft`, { params: { balance: bal } })
      .then(res => setFormData(prev => ({ ...prev, closure_email: { ...prev.closure_email, subject: res.data.subject, body: res.data.body } })))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  const blockedChecks = (records?.checks || []).filter(c => c.state === "blocked");

  const stepProblems = (step) => {
    if (step === 1) {
      if (!formData.matter_reference) return ["Choose the matter to close."];
      if (recordsStatus !== "loaded") return ["Wait for the records to load."];
      return [];
    }
    const all = outstandingItems(formData, records);
    if (step === 2) return all.filter(p => !p.startsWith("Confirm:") && !/closure email|being told/.test(p) && !p.includes("client's email"));
    if (step === 3) return all.filter(p => p.startsWith("Confirm:"));
    return all;
  };

  const handleNext = () => {
    const problems = stepProblems(currentStep);
    if (problems.length) { toast.error(problems[0], { duration: 6000 }); return; }
    setCurrentStep(prev => Math.min(prev + 1, totalSteps)); window.scrollTo(0, 0);
  };
  const handleBack = () => { setCurrentStep(prev => Math.max(prev - 1, 1)); window.scrollTo(0, 0); };

  const handleSubmit = async () => {
    const problems = outstandingItems(formData, records);
    if (problems.length) { problems.slice(0, 3).forEach(p => toast.error(p, { duration: 6000 })); return; }
    const payload = {
      ...formData,
      has_residual_balance: "no",
      closure_email: { ...formData.closure_email,
        to: formData.closure_email.to.split(/[,;]/).map(s => s.trim()).filter(Boolean) },
    };
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/submissions`, { data: payload });
      const sent = response.data?.closure_email_result;
      toast.success("File closed.");
      if (sent?.status === "sent") toast.success("Closure email sent to the client.");
      const sp = response.data?.sharepoint_archive;
      if (sp?.status === "archived") toast.success(`SharePoint folder moved to ${sp.archive}.`);
      else if (sp && !formData.is_test_submission) toast.error(`SharePoint folder not archived: ${sp.reason || sp.error || sp.status}. Try again from the closure record.`, { duration: 10000 });
      else if (formData.closure_email.send && !formData.is_test_submission)
        toast.error(`The closure email did not go: ${sent?.error || sent?.status || "unknown error"}. Tell the client another way.`, { duration: 10000 });
      navigate(`/submission/${response.data.id}`);
    } catch (error) {
      const d = error?.response?.data?.detail;
      if (d && Array.isArray(d.missing)) d.missing.slice(0, 3).forEach(m => toast.error(m, { duration: 8000 }));
      else if (d && Array.isArray(d.blocked)) d.blocked.forEach(m => toast.error(m, { duration: 8000 }));
      else toast.error(errorMessage(error, "Submission failed"));
    } finally { setIsSubmitting(false); }
  };

  const renderProgressBar = () => (
    <div className="mb-8">
      <div className="flex justify-between items-center mb-2">
        {[{ num: 1, label: "Matter" }, { num: 2, label: "Records" }, { num: 3, label: "Confirm" }, { num: 4, label: "Email and submit" }].map((step) => (
          <div key={step.num} className="flex flex-col items-center flex-1">
            <div className={`w-8 h-8 rounded-md flex items-center justify-center text-sm font-medium ${currentStep >= step.num ? 'bg-teal text-white' : 'bg-navy-hover text-txt-muted'}`}>{step.num}</div>
            <span className={`text-xs mt-1 hidden sm:block ${currentStep >= step.num ? 'text-teal' : 'text-txt-muted'}`}>{step.label}</span>
          </div>
        ))}
      </div>
      <div className="h-2 bg-navy-hover rounded-md overflow-hidden"><div className="h-full bg-gradient-to-r from-teal to-teal transition-all duration-300" style={{ width: `${((currentStep - 1) / (totalSteps - 1)) * 100}%` }} /></div>
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-3"><div className="p-2 rounded-md bg-blue-500/10"><Building2 className="w-5 h-5 text-blue-300" /></div><h2 className="text-xl font-semibold text-white">Which matter?</h2></div>
      <div>
        <Label className="text-sm font-medium text-txt">Matter *</Label>
        {/* Brief #4: partners close other fee earners' matters, so not fee earner scoped. */}
        <SmartClientMatterSearch
          label={null}
          placeholder="e.g., BL.VEC.002.0516"
          initialValue={formData.matter_reference}
          onSelect={(result) => {
            const ref = result.matter_reference || result.label || "";
            updateField("matter_reference", ref);
            if (result.client_name) updateField("client_name", result.client_name);
            if (ref) loadRecords(ref); else { setRecords(null); setRecordsStatus(null); }
          }}
        />
        {recordsStatus === "loading" && <p className="text-xs text-txt-muted mt-2 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Reading the ledger and compliance records...</p>}
        {recordsStatus === "error" && <p className="text-xs text-status-red mt-2">{recordsError}</p>}
      </div>
      {records && recordsStatus === "loaded" && (
        <div className="bg-navy-card border border-navy-border rounded-md p-4 grid grid-cols-2 gap-2 text-sm" data-testid="closure-matter-summary">
          <div className="text-txt-muted">Client</div><div className="text-white">{formData.client_name || "Not on record"}</div>
          <div className="text-txt-muted">Matter</div><div className="text-white">{records.matter_title || records.matter_reference}</div>
          <div className="text-txt-muted">Fee earner</div><div className="text-white">{formData.responsible_fee_earner || "Not on record"}</div>
          <div className="text-txt-muted">Closed by</div><div className="text-white">{formData.completer_email || "You"}</div>
          {blockedChecks.length > 0 && (
            <div className="col-span-2 mt-2 p-3 rounded-md border border-red-500/50 bg-status-red/10 text-status-red text-sm">
              <p className="font-medium flex items-center gap-1"><XCircle className="w-4 h-4" /> This file cannot close yet</p>
              <ul className="mt-1 space-y-1">{blockedChecks.map(c => <li key={c.key}>{c.detail}</li>)}</ul>
            </div>
          )}
        </div>
      )}
      <div className="p-4 bg-navy-card rounded-md border border-navy-border">
        <div className="flex items-start gap-3">
          <Checkbox id="is_test_submission" checked={formData.is_test_submission} onCheckedChange={(checked) => updateField("is_test_submission", checked === true)} data-testid="test-submission-checkbox" />
          <div>
            <Label htmlFor="is_test_submission" className="text-txt cursor-pointer flex items-center gap-2"><FlaskConical className="w-4 h-4 text-status-amber" />This is a test submission</Label>
            <p className="text-xs text-txt-muted mt-1">Marked with a [TEST] badge. The client is never emailed.</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => {
    const problems = outstandingItems(formData, records);
    return (
      <div className="space-y-6">
        <EmailStep formData={formData} setEmail={setEmail} />
        {problems.length > 0 ? (
          <div className="bg-status-red/10 border border-red-500/30 rounded-md p-4"><p className="font-medium text-status-red flex items-center gap-2"><XCircle className="w-5 h-5" />Cannot submit yet</p><ul className="mt-2 space-y-1">{problems.map((p, i) => <li key={i} className="text-status-red/80 text-sm">• {p}</li>)}</ul></div>
        ) : (
          <div className="bg-teal/10 border border-teal/30 rounded-md p-4 flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-teal mt-0.5" /><div><p className="font-medium text-teal">Ready to close {formData.matter_reference}</p><p className="text-teal/80 text-sm mt-1">CaseMinder checks the records again when you submit. The SharePoint folder is then renamed "CLOSED - ..." and moved into the practice area's CLOSED VIA CM folder.</p></div></div>
        )}
      </div>
    );
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return records ? <RecordsStep records={records} formData={formData} updateField={updateField} /> : null;
      case 3: return <ConfirmStep confirmations={records?.confirmations} formData={formData} setConfirmation={setConfirmation} />;
      case 4: return renderStep4();
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-navy">
      <nav className="sticky top-0 z-30 glass-nav border-b border-[rgba(99,102,241,0.25)]">
        <div className="max-w-4xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Bonsai" className="h-7" />
            <span className="text-white font-bold text-sm flex items-center gap-1.5"><FileX className="w-4 h-4" />File Closure</span>
          </div>
          <Button variant="ghost" onClick={() => navigate("/")} className="text-txt-muted hover:text-teal"><ArrowLeft className="w-4 h-4 mr-1.5" />Dashboard</Button>
        </div>
      </nav>
      <main className="max-w-4xl mx-auto px-6 py-8 animate-fade-in-up">
        <div className="glass-card p-6 sm:p-8">
          {renderProgressBar()}
          {renderCurrentStep()}
          <div className="flex justify-between mt-8 pt-6 border-t border-navy-border">
            <Button variant="outline" onClick={handleBack} disabled={currentStep === 1} className="gap-2 border-navy-border text-txt-muted hover:bg-navy-hover hover:text-white"><ArrowLeft className="w-4 h-4" />Back</Button>
            {currentStep < totalSteps ? (
              <Button onClick={handleNext} disabled={currentStep === 1 && blockedChecks.length > 0} className="bg-teal hover:bg-teal-hover text-white gap-2">Next<ArrowRight className="w-4 h-4" /></Button>
            ) : (
              <Button onClick={handleSubmit} disabled={isSubmitting || outstandingItems(formData, records).length > 0} className="bg-gold hover:bg-gold text-white gap-2" data-testid="submit-button">{isSubmitting ? (<><Loader2 className="w-4 h-4 animate-spin" />Submitting...</>) : (<><Send className="w-4 h-4" />Close the file{formData.closure_email.send ? " and email the client" : ""}</>)}</Button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
